import AutolinkerText from '@app/components/AutolinkerText'
import { DataList, DataListItem } from '@app/components/DataList'
import ExpandableContent from '@app/components/ExpandableContent'
import ExpirationIcon from '@app/components/ExpirationIcon'
import LotCard from '@app/components/LotCard'
import MarkupEmbeddedVideo from '@app/components/MarkupEmbeddedVideo'
import PhotoStrip from '@app/components/PhotoStrip'
import PillListRenderer from '@app/components/PillListRenderer'
import SpaceCard from '@app/components/SpaceCard'
import { ListingType } from '@app/models/enums'
import {
  formatBoolean,
  formatCurrency,
  formatDate,
  formatListingPrice,
  formatNumber,
  humanize,
} from '@app/utils/formatting'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link } from 'react-router-dom'
import { Row } from 'reactstrap'
import { ListingPageProps } from './ListingPage'

export default class ListingDetails extends React.PureComponent<
  ListingPageProps
> {
  public render() {
    return (
      <DocumentTitle title={`${this.props.listing.title} :: Details`}>
        <div>
          <PhotoStrip
            photos={this.props.listing.photos}
            propertyId={this.props.listing.property.id}
          />
          {this._renderGeneralInformation()}
          {this._renderSaleInformation()}
          {this._renderLeaseInformation()}
          {this._renderHighlights()}
          {this._renderPropertyDescription()}
          {this._renderPropertyInformation()}
          {this._renderSigns()}
          {this._renderListingServices()}
          {this._renderVideos()}
          {this._renderTopProspects()}
          {this._renderAdjoiningPropertyOwners()}
        </div>
      </DocumentTitle>
    )
  }

  private _renderAdjoiningPropertyOwners() {
    return (
      <div>
        <h5 className='section-title'>Adjoining Property Owners</h5>
        <div className='mx-3'>
          <PillListRenderer
            baseNavigateUrl='/connection'
            idValue='contactId'
            list={this.props.listing.adjoiningPropertyOwners}
          />
        </div>
      </div>
    )
  }

  private _renderGeneralInformation() {
    return (
      <div>
        <h5 className='section-title'>
          Property for {humanize(this.props.listing.type)}
        </h5>
        <div className='mx-3'>
          <Row className='ml-0' cols={1}>
            <div className='mr-5'>Property Types</div>
            <div className='ml-2'>
              <PillListRenderer
                noLink
                list={this.props.listing.property.propertyTypes.map(
                  (propType) => {
                    return {
                      name: humanize(propType),
                    }
                  },
                )}
              />
            </div>
          </Row>
          <DataList columnCount={2}>
            <DataListItem
              label='Status'
              value={humanize(this.props.listing.status)}
            />
            <DataListItem
              label='Listing Date'
              value={formatDate(
                this.props.listing.activeDate,
                'MM/DD/YYYY',
                'Not Set',
              )}
            />
            <DataListItem
              label='Expiration Date'
              value={
                <span>
                  {formatDate(
                    this.props.listing.expirationDate,
                    'MM/DD/YYYY',
                    'Not Set',
                  )}
                  <ExpirationIcon
                    className='ml-2'
                    date={this.props.listing.expirationDate}
                  />
                </span>
              }
            />
            <DataListItem
              label='Show on Website'
              value={formatBoolean(this.props.listing.showOnWebsite)}
            />
            <DataListItem
              label='Featured'
              value={formatBoolean(this.props.listing.isFeatured)}
            />
            <DataListItem
              label='File Position No'
              value={this.props.listing.filePositionNo}
            />
          </DataList>
          <h6 className='section-title'>Listing Agent(s)</h6>
          <PillListRenderer
            list={this.props.listing.listingAgents}
            baseNavigateUrl='/connection'
            idValue='contactId'
          />
        </div>
      </div>
    )
  }

  private _renderHighlights() {
    if (this.props.listing.highlights.size === 0) {
      return null
    }

    return (
      <div>
        <h5 className='section-title'>Highlights</h5>
        <ul>
          {this.props.listing.highlights.map((highlight, i) => {
            if (highlight === undefined || i === undefined) {
              return null
            }

            return (
              <AutolinkerText
                key={`highlight-${i}`}
                tag='li'
                text={highlight}
              />
            )
          })}
        </ul>
      </div>
    )
  }

  private _renderLeaseInformation() {
    if (
      this.props.listing.type !== ListingType.Lease ||
      this.props.listing.leaseData === undefined
    ) {
      return null
    }

    const leaseData = this.props.listing.leaseData

    return (
      <div>
        <h5 className='section-title'>Lease Information</h5>
        <div className='mx-3'>
          <DataList>
            <DataListItem
              label='Price'
              value={formatListingPrice(this.props.listing)}
            />
            <DataListItem
              label='Lease Term'
              value={`${formatNumber(leaseData.leaseTermInMonths, 0)} Mo`}
              visible={leaseData.leaseTermInMonths > 0}
            />
            <DataListItem
              label='Listing / Selling Fees'
              value={`${formatNumber(
                leaseData.commissionSplit.listingFee,
                2,
              )} / ${formatNumber(leaseData.commissionSplit.sellingFee, 2)}`}
            />
            <DataListItem
              label='Sublease'
              value={formatBoolean(leaseData.sublease)}
            />
            <DataListItem
              label='Build to Suit Option'
              value={formatBoolean(leaseData.buildToSuit)}
            />
            <DataListItem
              label='Date Available'
              value={formatDate(leaseData.dateAvailable, 'MM/DD/YYYY')}
              visible={leaseData.dateAvailable !== undefined}
            />
          </DataList>
        </div>
      </div>
    )
  }

  private _renderListingServices() {
    return (
      <div>
        <h5 className='section-title'>Listing Services</h5>
        <ul>
          {this.props.listing.listingServices.map((listingSvc, i) => {
            if (listingSvc === undefined || i === undefined) {
              return null
            }

            if (!listingSvc.link) {
              return null
            }

            return (
              <li key={`listing-svc-${i}`}>
                {listingSvc.name} -{' '}
                <AutolinkerText tag='span' text={listingSvc.link} />
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  private _renderPropertyDescription() {
    if (!this.props.listing.propertyDescription) {
      return null
    }

    return (
      <div>
        <h5 className='section-title'>Property Description</h5>
        <div className='mx-3'>
          <ExpandableContent>
            <div className='white-space-pre'>
              {this.props.listing.propertyDescription}
            </div>
          </ExpandableContent>
        </div>
      </div>
    )
  }

  private _renderPropertyInformation() {
    return (
      <div>
        <h5 className='section-title'>Property Information</h5>
        <div className='mx-3'>
          <DataList>
            <DataListItem
              label='Building Size'
              value={`${formatNumber(
                this.props.listing.property.totalBuildingSize,
                0,
              )} SF`}
              visible={this.props.listing.property.totalBuildingSize > 0}
            />
            <DataListItem
              label='Lot Size'
              value={this.props.listing.property.totalLotSize.toString()}
              visible={this.props.listing.property.totalLotSize.lotSize > 0}
            />
          </DataList>
          {this.props.listing.lot && (
            <LotCard className='mt-2' lot={this.props.listing.lot} />
          )}
          {this.props.listing.space && (
            <SpaceCard className='mt-2' space={this.props.listing.space} />
          )}
          {this.props.listing.property.locationDescription && (
            <div>
              <h6 className='section-title'>Location Description</h6>
              <ExpandableContent>
                <div className='white-space-pre'>
                  {this.props.listing.property.locationDescription}
                </div>
              </ExpandableContent>
            </div>
          )}
          <div className='mt-2'>
            <Link
              to={`/properties/property/${this.props.listing.property.id}`}
              target='_blank'
            >
              View full property information
            </Link>
          </div>
        </div>
      </div>
    )
  }

  private _renderSaleInformation() {
    if (
      this.props.listing.type === ListingType.Lease ||
      this.props.listing.saleData === undefined
    ) {
      return null
    }

    const saleData = this.props.listing.saleData

    const dataItems = [
      <DataListItem
        key='price'
        label='Price'
        value={formatListingPrice(this.props.listing)}
      />,
      <DataListItem
        key='listing-selling-fee'
        label='Listing / Selling Fees'
        value={`${formatNumber(
          saleData.commissionSplit.listingFee,
          2,
        )} / ${formatNumber(saleData.commissionSplit.sellingFee, 2)}`}
      />,
      <DataListItem
        key='cap-rate'
        label='Cap Rate'
        value={formatNumber(saleData.capRate, 2)}
      />,
    ]

    if (this.props.listing.type === ListingType.Sale) {
      dataItems.push(
        <DataListItem
          key='investment-property'
          label='Investment Property'
          value={formatBoolean(this.props.listing.isInvestmentProperty)}
        />,
        <DataListItem
          key='gross-income'
          label='Gross Income'
          value={formatCurrency(
            this.props.listing.financialData.grossIncome,
            0,
          )}
        />,
        <DataListItem
          key='net-income'
          label='Net Income'
          value={formatCurrency(this.props.listing.financialData.netIncome, 0)}
        />,
      )
    }

    return (
      <div>
        <h5 className='section-title'>Sale Information</h5>
        <div className='mx-3'>
          <DataList columnCount={2}>{dataItems}</DataList>
          {this.props.listing.financialData.rentDetails.size > 0 && (
            <div>
              <h6 className='section-title'>Rent Details</h6>
              {this.props.listing.financialData.rentDetails.map((tenant, i) => {
                if (tenant === undefined || i === undefined) {
                  return null
                }

                return (
                  <DataListItem
                    key={`tenant-${i}`}
                    label={tenant.description}
                    value={formatCurrency(tenant.rentAmount, 0)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  private _renderSigns() {
    const signs = this.props.listing.property.signs
      .map((s, i) => {
        if (s === undefined || i === undefined) {
          return null
        }

        return (
          <li key={`sign-${i}`}>
            {s.quantity} x {s.signType.name}
            <span> - </span>
            <span style={{ whiteSpace: 'pre-wrap' }}>{s.notes}</span>
          </li>
        )
      })
      .toJS()

    return (
      <div>
        <h5 className='section-title'>Signage</h5>
        <ul>{signs}</ul>
      </div>
    )
  }

  private _renderTopProspects() {
    return (
      <div>
        <h5 className='section-title'>Top Prospects</h5>
        <div className='mx-3'>
          <PillListRenderer
            baseNavigateUrl='/connection'
            idValue='contactId'
            list={this.props.listing.topProspects}
          />
        </div>
      </div>
    )
  }

  private _renderVideos() {
    return (
      <div>
        <h5 className='section-title'>Videos</h5>
        <div className='mx-3'>
          {this.props.listing.videoLinks.map((link, i) => {
            if (link === undefined || i === undefined) {
              return null
            }

            return (
              <div key={`video-${i}`} className='mb-3'>
                <MarkupEmbeddedVideo text={link} />
                <div className='mt-2'>
                  <AutolinkerText text={link} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}
